import { type Replacement, addSpanEvent, updateUrl, ImprHeaders } from '@imprivata-cloud/common';
import { ENVIRONMENT, TldToEnv } from '../featureFlagsUtils';
import { clientHeader, AMPortalService, CommonHeaders, type Endpoints, SearchParams, SystemType } from './constants';

export interface RequestInitImpr extends RequestInit {
  workflowId?: string | null;
  systemType?: SystemType;
}

/// Shared fetch wrapper
export const fetcher = async (
  endpoint: (typeof Endpoints)[keyof typeof Endpoints] | string,
  { workflowId, systemType, ...options }: RequestInitImpr,
  tenantId?: string,
) => {
  const tenantIdParam = tenantId
    ? tenantId
    : new URLSearchParams(window.location.search).get(SearchParams.TENANT_ID) || '';

  console.log('[fetch] systemType: ', systemType);

  const domainInfo = getTenantIdFromUrl(window.location.host);
  const tenantIdSubDomain = domainInfo?.tenantId;
  console.log(`Tenant subdomain: ${tenantIdSubDomain}`);
  const apiUrl = getApiUrl(tenantIdSubDomain ?? tenantIdParam);
  addSpanEvent(`fetching ${endpoint}`);

  return fetch(endpoint.startsWith('http') ? endpoint : `${apiUrl}${endpoint}`, {
    credentials: 'include',
    ...options,
    headers: {
      ...CommonHeaders,
      ...(options.headers || {}),
      ...(workflowId ? { 'Impr-Workflow-Id': workflowId } : {}),
      ...(systemType === SystemType.SETUP || endpoint.includes(AMPortalService.AM_PORTAL_SETUP)
        ? {
            [ImprHeaders.ImprClientName]: btoa(JSON.stringify({ name: clientHeader.SETUP })),
          }
        : {}),
    },
  })
    .then(async (response) => {
      if (response.ok) {
        return response
          .json()
          .then((json) => json)
          .catch(() => null); //response with empty body
      }
      const error = await response.json().then((json) => json);
      addSpanEvent(`error fetching ${endpoint}`, { error });
      return Promise.reject(error.error);
    })
    .catch((error) => {
      addSpanEvent(`error fetching ${endpoint}`, { error });
      console.error('Error fetching data:', endpoint, error);
      return Promise.reject(error);
    });
};

// Interpolate the API URL based on the current environment
export function getApiUrl(tenantId?: string): string {
  const host = tenantId ? tenantId : 'astra';
  const urls = _getUrl({ apiUrl: { sub: `${host}.sys` } });
  return urls?.apiUrl;
}

// Get the current environment and top-level domain from the current host
export function _getUrl(urlToBeGenerated: Record<string, Replacement>) {
  const url = window.location.host;

  if (url?.includes('localhost')) {
    return {
      apiUrl: 'http://localhost:8080',
      metadataUrl: 'https://metadata.app.dev.imprivata.cloud',
    };
  }

  let hostname = url;
  //Get tenantId if present
  const domainInfo = getTenantIdFromUrl(url);

  if (domainInfo) {
    hostname = domainInfo.urlWithoutTenantId;
  }

  const customRegex = /^(?:https?:\/\/)?([A-Za-z0-9_]+)\w+/;
  const urlRegex = hostname?.includes('.app') ? undefined : customRegex;
  return updateUrl(hostname, urlToBeGenerated, 'https://', urlRegex);
}

/**
 * Checks if a hostname contains a tenant ID
 * @param hostname The hostname to check
 * @returns Boolean indicating if tenant ID is present
 */
export function getTenantIdFromUrl(hostname: string): { tenantId: string; urlWithoutTenantId: string } | null {
  // Split the hostname into parts
  const parts = hostname.split('.');

  // Identify common domain patterns
  const domainParts = 2; // e.g., imprivata.com, imprivata.cloud, imprivata.eu
  const envPart = parts.some((part) => /^(dev|int|stg|eph|prod)(\d+)?$/.test(part)) ? 1 : 0;
  const appPart = parts.some((part) => part === 'app') ? 1 : 0;

  // Standard URL has: service + optional app + optional env + domain
  // e.g., access.app.dev.imprivata.cloud has 1+1+1+2=5 parts
  const standardParts = 1 + appPart + envPart + domainParts;

  // If we have more parts than the standard pattern, assume the first is a tenant ID
  if (parts.length > standardParts) {
    const tenantIdPart = parts[0];
    const urlWithoutTenantId = parts.slice(1).join('.');
    return { tenantId: tenantIdPart, urlWithoutTenantId };
  }

  return null;
}
export function _getEnv(): ENVIRONMENT {
  const url = window.location.host;
  const match = url.match(/\b(int|dev|stg)\b/);
  if (match) {
    return ENVIRONMENT[match[1] as keyof typeof ENVIRONMENT];
  }
  if (url.includes('localhost')) {
    return ENVIRONMENT.local;
  }
  // Check for TLD
  const tldMatch = url.match(/\.([a-z]+)$/i);
  const tld = tldMatch ? tldMatch[1] : null;
  return tld ? TldToEnv[tld] ?? ENVIRONMENT.prod : ENVIRONMENT.prod;
}
